/**
 * Route copy for `src/routes/team` (keys: `routes.team.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  acceptOrgInvitePage: {
    invalidInvitation: 'Invalid invitation',
    invalidInvitationLinkMissingToken: 'Invalid invitation link (missing token).',
    invitationAlreadyUsed: 'Invitation already used',
    invitationExpired: 'Invitation expired',
    invitationNotFound: 'Invitation not found',
    thisInvitationIsNoLongerValid: 'This invitation is no longer valid.',
    couldNotLoadTheInvitationRight: 'Could not load the invitation right now.',
    yourMembershipHasBeenRecordedUnder: 'Your membership has been recorded under the organization that invited you. Sign in again to access it.',
    invitationAcceptedYouAreNowPart: 'Invitation accepted — you are now part of the team.',
    thisInvitationCannotBeFound: 'This invitation cannot be found.',
    thisInvitationHasExpiredOrHas: 'This invitation has expired or has already been used.',
    youAreAlreadyAMemberOf: 'You are already a member of this organization.',
    couldNotAcceptTheInvitationError: 'Could not accept the invitation (error {{status}}).',
    couldNotAcceptTheInvitationRight: 'Could not accept the invitation right now.',
    loadingYourInvitation: 'Loading your invitation…',
    finalizingYourMembership: 'Finalizing your membership…',
    youReInvitedToKarexia: 'You\'re invited to Karexia',
    join: 'Join',
    as: 'as',
    invitationSentTo: '. Invitation sent to',
    iAlreadyHaveAnAccountSign: 'I already have an account — Sign in',
    createAnAccount: 'Create an account',
    signInOrSignUpWith: 'Sign in or sign up with the address',
    toAccept: 'to accept.',
    validatingYourInvitation: 'Validating your invitation…',
    goToTheDashboard: 'Go to the dashboard',
    reconnectionRequired: 'Reconnection required',
    signInAgain: 'Sign in again',
    backToTheApp: 'Back to the app',
  },
  teamPage: {
    invitationSent: 'Invitation sent',
    anEmailWasSentTo: 'An email was sent to',
    orgRoleLabelsYouCanAlso: '({{ORG_ROLE_LABELS}}). You can also share this link directly:',
    invitationLink: 'Invitation link',
    copied: 'Copied ✓',
    copy: 'Copy',
    couldNotCreateTheInvitationCheck: 'Could not create the invitation. Check the address and try again.',
    inviteAMember: 'Invite a member',
    emailAddress: 'Email address',
    colleagueExampleCom: 'colleague@example.com',
    role: 'Role',
    cancel: 'Cancel',
    sending: 'Sending…',
    createInvitation: 'Create invitation',
    couldNotChangeTheRoleInsufficient: 'Could not change the role (insufficient rights or last owner).',
    couldNotRemoveThisMemberLast: 'Could not remove this member (last owner?).',
    invitationResent: 'Invitation resent.',
    couldNotResendTheInvitation: 'Could not resend the invitation.',
    invitationRevoked: 'Invitation revoked.',
    couldNotRevokeTheInvitation: 'Could not revoke the invitation.',
    you: '(you)',
    resendInvitation: 'Resend invitation',
    revoke: 'Revoke',
    youCannotChangeYourOwnRole: 'You cannot change your own role here.',
    memberRole: 'Member role',
    remove: 'Remove',
    removeMember: 'Remove member',
    removeEmailFromTheOrganization: 'Remove {{email}} from the organization?',
    members: 'Members',
    inviteColleaguesManageTheirRoleAnd: 'Invite colleagues, manage their role and share your CRM data with them.',
    adminOrOwnerRoleRequiredTo: 'Admin or owner role required to invite.',
    invite: '+ Invite',
    couldNotLoadTheTeamTry: 'Could not load the team. Try again later.',
    loadingTheTeam: 'Loading the team…',
    noMembers: 'No members.',
    youReAloneInThisOrganization: 'You\'re alone in this organization — invite colleagues to collaborate.',
    externalManagersDelegation: 'External managers (delegation)',
    handTheManagementOfYourPosts: 'Hand the management of your posts and ads to a person outside your organization, with a very granular permission (e.g. publish on Facebook but not on LinkedIn). They keep their own account and only see what you grant — access is logged and revocable at any time.',
    manageDelegations: 'Manage delegations',
  },
};

export default en;
